import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
  useEffectEvent,
} from "react";
import Cookies from "js-cookie";

import { setUserContext as setObservabilityUserCtx } from "@wisnio/observability/browser";
import { useLazyViewer } from "@/features/workspace/hooks/useViewer";
import { useLocationUpdate } from "@/shared/utils/hooks/useLocationUptate";
import { toNumber } from "lodash";
import { useLoadInitialAccount } from "@/features/workspace/hooks/useAccount";
import AccountFrozenOverlay from "@/shared/widgets/AccountFrozenOverlay";
import {
  showFullPageLoader,
  hideFullPageLoader,
} from "@/shared/lib/full-page-loader";
import useIntercom from "@/lib/intercom/useIntercom";
import { useTranslation } from "react-i18next";
import { useAccountOrganizations } from "@/features/workspace/hooks/useAccountOrganizations";
import { useLocation, useNavigate } from "react-router";
import { setUserContext as setLoggerUserContext } from "@wisnio/logger/browser";
import { setUserContext as setFeatureUserContext } from "@wisnio/feature-flags/browser";

interface AuthContextProps {
  isAuthenticated: boolean;
}

interface AuthProviderProps {
  children?: React.ReactNode;
}

const AuthContext = createContext({} as AuthContextProps);

function AuthProvider(props: AuthProviderProps) {
  const [isLoading, setIsLoading] = useState(true);
  const location = useLocation();
  const navigate = useNavigate();

  const { children } = props;

  const { t } = useTranslation();
  const { fetchViewer } = useLazyViewer();
  const { handleLoadInitialAccount } = useLoadInitialAccount();
  const { initializeIntercom } = useIntercom();
  const accountId = toNumber(Cookies.get("account_id"));
  const isAuthenticated =
    !!Cookies.get("_s") && !!Cookies.get("authorized_person");

  // Get organizations for the current account to check URL accessibility
  const { organizations } = useAccountOrganizations(accountId);

  useLocationUpdate();

  useEffect(() => {
    if (isAuthenticated) {
      handleUserAndAccountInit();
    } else {
      setIsLoading(false);
      hideFullPageLoader();
    }
  }, [isAuthenticated]);

  // Effect Event for checking URL accessibility
  // This non-reactive logic reads the latest data without causing effect re-runs
  const checkUrlAccessibility = useEffectEvent(() => {
    // Don't check if not authenticated or still loading
    if (!isAuthenticated || isLoading) {
      return;
    }

    // Don't check if we don't have account ID yet
    if (!accountId) {
      return;
    }

    // Don't check if organizations are still loading or empty
    if (!organizations || organizations.length === 0) {
      return;
    }

    const { pathname } = location;

    // Don't check if already on /org page (to avoid redirect loops)
    if (pathname === "/org") {
      return;
    }

    // Check if path starts with /org/:orgId
    const orgPathMatch = pathname.match(/^\/org\/(\d+)/);
    if (!orgPathMatch) {
      return;
    }

    const orgIdFromUrl = toNumber(orgPathMatch[1]);
    const orgExistsInAccount = organizations.some(
      (org) => org?.id === orgIdFromUrl
    );

    if (!orgExistsInAccount && orgIdFromUrl) {
      // Save the intended URL for redirect after account/org selection
      const intendedUrl = `${pathname}${location.search}`;
      localStorage.setItem("accountSwitchRedirect", intendedUrl);
      navigate("/org", { replace: true });
    }
  });

  // Check if the URL's organization belongs to the current account
  // Only runs when location changes, but reads latest data from the effect event
  useEffect(() => {
    checkUrlAccessibility();
  }, [location]);

  const handleUserAndAccountInit = useCallback(async () => {
    showFullPageLoader(t("auth.verifying_session"));
    const { data } = await fetchViewer();

    const accountId = toNumber(Cookies.get("account_id"));
    if (accountId) {
      showFullPageLoader(t("auth.loading_account"));
      const accountData = await handleLoadInitialAccount(accountId);

      // Set logo CSS variable if account has a custom logo
      if (accountData?.data?.account?.config?.logo) {
        document.documentElement.style.setProperty(
          "--logo_url",
          `url(${
            accountData.data.account.config.logo ||
            import.meta.env.VITE_APP_DEFAULT_LOGO
          })`
        );
      }
    }

    if (data?.viewer && data.viewer.email) {
      showFullPageLoader(t("auth.setting_up_workspace"));
      await initializeIntercom(data.viewer);

      if (data.viewer.id) {
        setObservabilityUserCtx({
          user_id: data.viewer.id,
          contact_email: data.viewer.email,
          first_name: data.viewer.first_name ?? "",
          last_name: data.viewer.last_name ?? "",
        });
        setLoggerUserContext({
          id: String(data.viewer.id),
          email: data.viewer.email,
        });
        setFeatureUserContext(data.viewer.id, data.viewer.email);
      }
    }

    setIsLoading(false);
    hideFullPageLoader();
  }, [initializeIntercom, fetchViewer, handleLoadInitialAccount, t]);

  const contextValue = {
    isAuthenticated,
  };

  return (
    <AuthContext.Provider value={contextValue}>
      {!isLoading && <AccountFrozenOverlay>{children}</AccountFrozenOverlay>}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);

export default AuthProvider;
